import { Modal } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { useShouldFieldBeHighlighted } from "../../../hooks/useShouldFieldBeHighlighted";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import type { Site } from "../../../utils/backend-types";
import { SiteForm } from "./SiteForm/SiteForm";

interface SiteModalProps {
  headerText: string;
  site: Site | null;
  variantId: number;
  isOpen: boolean;
  onToggle: () => void;
  onClickSave?: (site: Site) => void;
}
function SiteModal({
  headerText,
  site,
  variantId,
  isOpen,
  onToggle,
  onClickSave
}: SiteModalProps) {
  // footerNode is used in CustomForm with React.Portal to allow the
  //  CustomForm buttons to be displayed outside of the CustomForm and in this component
  // footerRef and useCallback are needed because the node does not exist in the Dom
  //  at the time of the first render of this component
  const [footerNode, setFooterNode] = useState(null);
  const { state: missingData } = useLocation();
  const queryClient = useQueryClient();

  const missingFields =
    (missingData && missingData.missingFields.general) || [];

  const shouldFieldBeHighlighted = useShouldFieldBeHighlighted(missingFields);

  const footerRef = useCallback((node) => {
    if (node !== null) {
      setFooterNode(node);
    }
  }, []);

  function handleSubmit(site) {
    onClickSave?.(site);
    queryClient.invalidateQueries({
      queryKey: ["siteCategories"]
    });
    onToggle();
  }

  return (
    <Modal
      className="SiteModal"
      opened={isOpen}
      title={headerText}
      onClose={onToggle}
    >
      {footerNode && (
        <SiteForm
          buttonContainer={footerNode}
          missingFields={missingFields}
          shouldFieldBeHighlighted={shouldFieldBeHighlighted}
          site={site}
          variant={variantId}
          onCancel={onToggle}
          onSubmit={handleSubmit}
        />
      )}
      <ModalFooter ref={footerRef} />
    </Modal>
  );
}

export { SiteModal, SiteModalProps };
